import React, { useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { expValEquals } from '@confluence/feature-experiments';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys as LOCALSTORAGE_KEYS } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import {
	i18n,
	onClickLearnMore,
	type MigrationSuccessFlagComponentProps,
	MigrationSuccessFlagComponentV2,
} from './MigrationSuccessFlagComponentV2.tsx';

// Note: Old component which shows the success flag only after a successful manual conversion; being phased out in favor of MigrationSuccessFlagComponentV2
const MigrationSuccessFlagComponent = ({
	contentId,
	flags,
}: MigrationSuccessFlagComponentProps) => {
	// We only want to show the flag once (we don't want to re-render and show the flag again)
	const didShowFlag = useRef<boolean>(false);
	// This key is set previously when a user manually converts a page (i.e. clicks "Convert page")
	const didManualConversionRecently =
		localStorage.getItem(LOCALSTORAGE_KEYS.PAGE_JUST_CONVERTED) === contentId;
	// We want to clear the previously set key since we only want to show the flag when the user first lands in the Fabric editor
	const onClose = () => localStorage.removeItem(LOCALSTORAGE_KEYS.PAGE_JUST_CONVERTED);

	const shouldShowFlag = useMemo(
		() => didManualConversionRecently && !!flags,
		[didManualConversionRecently, flags],
	);

	if (shouldShowFlag && !didShowFlag.current) {
		didShowFlag.current = true;
		void flags.showInfoFlag({
			title: <FormattedMessage {...i18n.pageHasBeenConvertedTitle} />,
			description: <FormattedMessage {...i18n.pageHasBeenConvertedDescription} />,
			actions: [
				{
					content: <FormattedMessage {...i18n.pageHasBeenConvertedLearnMore} />,
					onClick: onClickLearnMore,
				},
			],
			// Auto-dismiss the flag after 8 seconds (AtlasKit AutoDismissFlag default dismiss delay)
			isAutoDismiss: true,
			onClose,
		});
	}

	return null;
};

const MigrationSuccessFlagComponentSwitch = (props: MigrationSuccessFlagComponentProps) =>
	expValEquals('confluence_auto_conversion_success_flag', 'isEnabled', true) ? (
		<MigrationSuccessFlagComponentV2 {...props} />
	) : (
		<MigrationSuccessFlagComponent {...props} />
	);

export const MigrationSuccessFlag = withFlags(MigrationSuccessFlagComponentSwitch);
